import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { hasBillingRole } from '../../../utils/roles';
import { useNavigate, useParams } from 'react-router-dom';
import { adminAPI } from '../../../services/api';
import api from '../../../services/api';
import { 
  COUNTRIES, 
  getStatesForCountry, 
  formatPhoneNumber, 
  validatePhoneNumber, 
  validatePostalCode,
  formatPostalCode
} from '../../../utils/addressData';
import StepProgress from '../../../components/StepProgress';
import {
  FEE_TYPES_FLAT,
  BILLING_UNITS as CATALOG_BILLING_UNITS,
  FEE_SCHEDULES as CATALOG_FEE_SCHEDULES,
  STORAGE_CLASSES as CATALOG_STORAGE_CLASSES
} from '../../../utils/customerFeeCatalog';

const CustomerForm = () => {
  const { user: authUser } = useAuth();
  const canCreateCustomer = hasBillingRole(authUser);
  const defaultNotificationPreferences = {
    request_created: true,
    request_status: true,
    invoice_created: true,
    invoice_status: true,
    manifest_updates: true,
    crossdock_updates: true,
    receiving_updates: true,
    shipment_updates: true
  };
  const defaultServiceCapabilities = {
    incoming: true,
    inbound_project: true,
    ship: true,
    crossdock: true,
    pick_pack: true,
    kit_build: true,
    require_request_approval: false
  };
  const defaultReceivingPreferences = {
    require_serial_numbers_on_receive: false,
    require_mac_addresses_on_receive: false,
    relabel_on_receive: false
  };
  const serviceCapabilityOptions = [
    ['incoming', 'Incoming Inventory'],
    ['inbound_project', 'Inbound Manifest'],
    ['ship', 'Ship Products'],
    ['crossdock', 'Cross-Dock'],
    ['pick_pack', 'Pick & Pack'],
    ['kit_build', 'Kit Building']
  ];
  const navigate = useNavigate();
  const { id } = useParams(); // Get customer ID from URL for edit mode
  const isEditing = Boolean(id); // Determine if we're editing or creating
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);
  const [phoneError, setPhoneError] = useState('');
  const [postalCodeError, setPostalCodeError] = useState('');
  
  // Form state - separated into company and user data
  const [companyData, setCompanyData] = useState({
    name: '',
    email: '',
    notificationEmail: '',
    notificationEmailsText: '',
    notificationPreferences: defaultNotificationPreferences,
    receivingPreferences: defaultReceivingPreferences,
    serviceCapabilities: defaultServiceCapabilities,
    phone: '',
    address: {
      street: '',
      addressLine2: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'United States'
    },
    isActive: true,
    notes: '',
    billingInfo: {
      billingContactName: '',
      billingContactEmail: '',
      billingContactPhone: ''
    },
    zohoCustomerId: ''
  });

  const [userData, setUserData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: 'customer_admin' // Always set to customer_admin for primary contact
  });

  // Warehouse and fees state
  const [availableWarehouses, setAvailableWarehouses] = useState([]);
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);
  const [customerFees, setCustomerFees] = useState([]);
  // Flat-rate storage override. When enabled, on customer create we
  // POST to /admin/customers/:id/storage-agreements after the
  // customer is created to create the initial agreement. Per-pallet
  // storage rows are still allowed to be configured for the
  // customer (they just won't be billed while the agreement is
  // active) so ops can switch the customer back to per-pallet later
  // without re-entering rates.
  const [flatRateStorage, setFlatRateStorage] = useState({
    enabled: false,
    sqft: '',
    monthly_amount: '',
    notes: ''
  });
  
  // Fee metadata imported from the shared catalog. Under the
  // simplified catalog `FEE_TYPES_FLAT` only contains Storage,
  // Handling (inbound/outbound/labour), so the wizard inherits the
  // shrunken list automatically. All four are flagged required.
  const REQUIRED_FEE_TYPES = new Set([
    'storage_fee',
    'handling_inbound',
    'handling_outbound',
    'labour'
  ]);
  const FEE_TYPES = FEE_TYPES_FLAT.map((fee) => ({
    value: fee.value,
    label: fee.label,
    description: fee.description,
    defaultUnit: fee.defaultBillingUnit,
    required: REQUIRED_FEE_TYPES.has(fee.value),
    category: fee.category
  }));

  // Storage_fee needs one row per storage_class. Under the simplified
  // catalog we surface two options: Standard Pallet (40×48×60) and
  // Oversized Pallet (45×45×75). Each gets its own row in the
  // wizard keyed by the composite (fee_type, storage_class) so the
  // customer can have different rates for each.
  const STORAGE_CLASSES_IN_WIZARD = [
    { value: 'standard', label: 'Standard Pallet', description: '40"×48"×60"' },
    { value: 'oversize', label: 'Oversized Pallet', description: '45"×45"×75"' }
  ];
  const makeFeeKey = (feeType, storageClass) => {
    if (feeType === 'storage_fee') return `storage_fee|${storageClass || 'standard'}`;
    return feeType;
  };
  // Helper for lookup: find an existing row in customerFees that
  // matches a (fee_type, storage_class) pair. For non-storage types
  // storage_class is ignored.
  const findFeeRow = (feeType, storageClass = null) => {
    if (feeType === 'storage_fee') {
      return customerFees.find((f) => f.fee_type === 'storage_fee' && (f.storage_class || 'standard') === storageClass);
    }
    return customerFees.find((f) => f.fee_type === feeType);
  };

  const BILLING_UNITS = CATALOG_BILLING_UNITS;
  const FEE_SCHEDULES = CATALOG_FEE_SCHEDULES;
  // Ensure the empty-string "Standard/Rackable" option is replaced
  // with the canonical 'standard' value so new rows save off-spec
  // values. The legacy empty-string value still round-trips because
  // Sequelize coerces it to null on write, but this keeps the form
  // honest.
  const STORAGE_CLASSES = CATALOG_STORAGE_CLASSES;

  // Handle company data changes
  const handleCompanyChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setCompanyData({ ...companyData, [name]: checked });
    } else if (name.includes('.')) {
      // Handle nested objects (address and billingInfo)
      const [parent, child] = name.split('.');
      
      let newValue = value;
      
      // Special handling for phone number formatting
      if (name === 'address.phone' || name === 'phone') {
        newValue = formatPhoneNumber(value);
        const validation = validatePhoneNumber(newValue);
        if (!validation.isValid) {
          setPhoneError(validation.error);
        } else {
          setPhoneError('');
        }
      }
      
      // Special handling for postal code formatting
      if (child === 'postalCode') {
        const country = companyData.address.country;
        newValue = formatPostalCode(value, country);
        const validation = validatePostalCode(newValue, country);
        if (!validation.isValid) {
          setPostalCodeError(validation.error);
        } else {
          setPostalCodeError('');
        }
      }
      
      setCompanyData({
        ...companyData,
        [parent]: {
          ...companyData[parent],
          [child]: newValue
        }
      });
      
      // Reset state when country changes
      if (name === 'address.country') {
        setCompanyData({
          ...companyData,
          address: {
            ...companyData.address,
            country: value,
            state: '' // Reset state when country changes
          }
        });
      }
    } else {
      // Handle phone number for top-level phone field
      if (name === 'phone') {
        const formattedPhone = formatPhoneNumber(value);
        const validation = validatePhoneNumber(formattedPhone);
        if (!validation.isValid) {
          setPhoneError(validation.error);
        } else {
          setPhoneError('');
        }
        setCompanyData({ ...companyData, [name]: formattedPhone });
      } else {
        setCompanyData({ ...companyData, [name]: value });
      }
    }
  };

  // Handle user data changes
  const handleUserChange = (e) => {
    const { name, value } = e.target;
    
    // Handle phone number formatting for user data
    if (name === 'phone') {
      const formattedPhone = formatPhoneNumber(value);
      setUserData({ ...userData, [name]: formattedPhone });
    } else {
      setUserData({ ...userData, [name]: value });
    }
  };

  // Handle warehouse selection
  const handleWarehouseSelection = (warehouseId) => {
    const warehouse = availableWarehouses.find(w => w.id === warehouseId);
    if (warehouse && !selectedWarehouses.find(w => w.id === warehouseId)) {
      setSelectedWarehouses([...selectedWarehouses, warehouse]);
    } else if (selectedWarehouses.find(w => w.id === warehouseId)) {
      // Remove warehouse if already selected
      setSelectedWarehouses(selectedWarehouses.filter(w => w.id !== warehouseId));
    }
  };

  // Remove warehouse selection
  const removeWarehouseSelection = (warehouseId) => {
    setSelectedWarehouses(selectedWarehouses.filter(w => w.id !== warehouseId));
    // Remove associated fees from the form if any
    setCustomerFees(customerFees.filter(f => f.warehouse_id !== warehouseId));
  };

  // Customer fee helper functions are handled inline now in the form

  // Update customer fee (still needed for the form)
  const updateCustomerFee = (feeId, field, value) => {
    setCustomerFees(customerFees.map(f => 
      f.id === feeId ? { ...f, [field]: value } : f
    ));
  };

  // Validate step 1 (company data)
  const validateStep1 = () => {
    if (!companyData.name.trim()) {
      setError('Company name is required');
      return false;
    }
    
    // Validate phone if provided
    if (companyData.phone) {
      const phoneValidation = validatePhoneNumber(companyData.phone);
      if (!phoneValidation.isValid) {
        setError(phoneValidation.error);
        return false;
      }
    }
    
    // Validate postal code if provided
    if (companyData.address.postalCode) {
      const postalValidation = validatePostalCode(companyData.address.postalCode, companyData.address.country);
      if (!postalValidation.isValid) {
        setError(postalValidation.error);
        return false;
      }
    }
    
    setError(null);
    return true;
  };

  // Validate step 2 (user data)
  const validateStep2 = () => {
    if (!userData.firstName.trim() || !userData.lastName.trim()) {
      setError('First and last name are required');
      return false;
    }
    if (!userData.email.trim()) {
      setError('Contact email is required');
      return false;
    }
    
    // Validate user phone if provided
    if (userData.phone) {
      const phoneValidation = validatePhoneNumber(userData.phone);
      if (!phoneValidation.isValid) {
        setError(phoneValidation.error);
        return false;
      }
    }
    
    setError(null);
    return true;
  };

  // Validate step 3 (warehouse and fees)
  const validateStep3 = () => {
    if (selectedWarehouses.length === 0) {
      setError('Please select at least one warehouse for customer access');
      return false;
    }

    setError(null);
    return true;
  };

  // Handle next step
  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3); // Future: warehouse assignment step
    }
  };

  // Handle previous step
  const handlePrevious = () => {
    if (step > 1) {
      setStep(step - 1);
      setError(null);
    }
  };

  // Load existing customer data when editing
  useEffect(() => {
    const loadCustomerData = async () => {
      if (!isEditing || !id) return;
      
      try {
        setLoading(true);
        const response = await adminAPI.getCustomer(id);
        const customer = response?.data || response;
        
        if (!customer) {
          throw new Error('No customer data received');
        }
        
        // Handle address which might be stored as a JSON string or object or individual fields
        let addressObj = {
          street: '',
          addressLine2: '',
          city: '',
          state: '',
          postalCode: '',
          country: 'United States'
        };
        
        if (customer.address) {
          if (typeof customer.address === 'string') {
            try {
              addressObj = { ...addressObj, ...JSON.parse(customer.address) };
            } catch (e) {
              // Failed to parse address JSON
            }
          } else {
            addressObj = { ...addressObj, ...customer.address };
          }
        } else {
          // Handle individual address fields from new schema
          addressObj = {
            street: customer.street_address || '',
            addressLine2: customer.address_line_2 || '',
            city: customer.city || '',
            state: customer.state_province || '',
            postalCode: customer.postal_code || '',
            country: customer.country || 'United States'
          };
        }
        
        // Handle billing info
        let billingInfoObj = {
          billingContactName: '',
          billingContactEmail: '',
          billingContactPhone: ''
        };
        
        if (customer.billingInfo) {
          if (typeof customer.billingInfo === 'string') {
            try {
              billingInfoObj = {
                ...billingInfoObj,
                ...JSON.parse(customer.billingInfo)
              };
            } catch (e) {
              // Failed to parse billingInfo JSON
            }
          } else {
            billingInfoObj = {
              ...billingInfoObj,
              ...customer.billingInfo
            };
          }
        }

        // Find primary contact from Users array
        let primaryUser = null;
        if (customer.Users && Array.isArray(customer.Users)) {
          primaryUser = customer.Users.find(user => user.is_primary === true) || customer.Users[0];
        }
        
        setCompanyData({
          name: customer.name || '',
          email: customer.email || '',
          notificationEmail: customer.notification_email || '',
          notificationEmailsText: Array.isArray(customer.notification_emails) ? customer.notification_emails.join('\n') : '',
          notificationPreferences: { ...defaultNotificationPreferences, ...(customer.notification_preferences || {}) },
          receivingPreferences: { ...defaultReceivingPreferences, ...(customer.receivingPreferences || customer.receiving_preferences || {}) },
          serviceCapabilities: { ...defaultServiceCapabilities, ...(customer.serviceCapabilities || customer.service_capabilities || {}) },
          phone: customer.phone || '',
          address: addressObj,
          isActive: customer.is_active !== undefined ? customer.is_active : true,
          notes: customer.notes || '',
          billingInfo: billingInfoObj,
          zohoCustomerId: customer.zohoCustomerId || ''
        });

        if (primaryUser) {
          const nameParts = primaryUser.name ? primaryUser.name.split(' ') : ['', ''];
          setUserData({
            firstName: nameParts[0] || '',
            lastName: nameParts.slice(1).join(' ') || '',
            email: primaryUser.email || '',
            phone: primaryUser.phone || '',
            password: '',
            confirmPassword: '',
            role: primaryUser.role || 'customer_admin'
          });
        }
      } catch (err) {
        console.error('Error loading customer data:', err);
        setError('Failed to load customer data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    loadCustomerData();
  }, [isEditing, id]);

  // Load available warehouses
  useEffect(() => {
    const loadWarehouses = async () => {
      try {
        const response = await adminAPI.getAdminWarehouses();
        // Handle both array response and object with warehouses property
        const warehousesData = Array.isArray(response) ? response : (response.warehouses || []);
        setAvailableWarehouses(warehousesData);
      } catch (err) {
        console.error('Error loading warehouses:', err);
        setError('Failed to load warehouses. Please try again.');
        setAvailableWarehouses([]);
      }
    };
    
    if (!isEditing) {
      loadWarehouses();
    }
  }, [isEditing]);

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate current step
    if ((step === 2 && isEditing && !validateStep2()) || 
        (step === 3 && !isEditing && (!validateStep2() || !validateStep3()))) {
      return;
    }
    
    setIsSubmitting(true);
    setError(null);

    try {
      // Prepare the data for submission
      const submissionData = {
        companyData: {
          companyName: companyData.name,
          email: companyData.email || companyData.notificationEmail || userData.email,
          notificationEmail: companyData.notificationEmail,
          notificationEmails: companyData.notificationEmailsText
            .split(/[\n,;]/)
            .map((email) => email.trim())
            .filter(Boolean),
          notificationPreferences: companyData.notificationPreferences,
          receivingPreferences: companyData.receivingPreferences,
          serviceCapabilities: companyData.serviceCapabilities,
          phone: companyData.phone,
          address: companyData.address,
          billingInfo: companyData.billingInfo,
          notes: companyData.notes,
          is_active: companyData.isActive,
          zohoCustomerId: companyData.zohoCustomerId
        },
        userData: {
          contactName: `${userData.firstName} ${userData.lastName}`.trim(),
          email: userData.email,
          phone: userData.phone,
          password: userData.password
        }
      };

      let response;
      if (isEditing) {
        response = await adminAPI.updateCustomer(id, submissionData);
      } else {
        response = await adminAPI.createCustomer(submissionData);
        
        // For new customers, also setup destination warehouse access and fees
        if (response?.data?.company?.id || response?.company?.id) {
          const companyId = response?.data?.company?.id || response?.company?.id;
          
          try {
            await api.post(`/admin/customers/${companyId}/warehouse-access`, {
              destinationWarehouseAccesses: selectedWarehouses.map(w => ({
                warehouse_id: w.id
              })),
              customerFees: selectedWarehouses.flatMap(warehouse =>
                customerFees
                  .filter((fee) => fee.fee_amount !== '' && fee.fee_amount !== null && fee.fee_amount !== undefined && parseFloat(fee.fee_amount) > 0)
                  .map(fee => ({
                    warehouse_id: warehouse.id,
                    fee_type: fee.fee_type,
                    fee_amount: fee.fee_amount,
                    // RF12: only the catalog's supported pair is
                    // implemented; force it regardless of stale state.
                    billing_unit: (FEE_TYPES.find((t) => t.value === fee.fee_type)?.defaultUnit) || fee.billing_unit,
                    fee_schedule: fee.fee_type === 'storage_fee' ? 'monthly' : 'one_time',
                    // Preserve the storage_class on each row so
                    // standard vs oversize rates land as distinct
                    // CustomerFees records (composite unique on
                    // company+warehouse+type+storage_class).
                    storage_class: fee.fee_type === 'storage_fee' ? (fee.storage_class || 'standard') : null,
                    notes: `${fee.fee_type}${fee.storage_class ? ` (${fee.storage_class})` : ''} for ${warehouse.name}`
                  }))
              )
            });
          } catch (warehouseErr) {
            console.error('Error setting up destination warehouse access:', warehouseErr);
            console.error('Warehouse access error details:', warehouseErr.response?.data || warehouseErr.message);
            // Don't navigate away -- show the error so the admin knows
            // the warehouse setup failed and can retry from customer detail.
            setError(`Customer created but destination warehouse setup failed: ${warehouseErr.response?.data?.message || warehouseErr.message}. You can configure destination warehouses from the customer details page.`);
            setIsSubmitting(false);
            return; // Stop here so the error is visible
          }

          // Flat-rate storage agreement (optional). Created after the
          // warehouse access + fees so historical per-pallet rates
          // are still on the customer record if ops switches back.
          if (flatRateStorage.enabled
              && flatRateStorage.sqft
              && flatRateStorage.monthly_amount
              && Number(flatRateStorage.sqft) > 0
              && Number(flatRateStorage.monthly_amount) >= 0) {
            try {
              await api.post(`/admin/customers/${companyId}/storage-agreements`, {
                sqft: Number(flatRateStorage.sqft),
                monthly_amount: Number(flatRateStorage.monthly_amount),
                notes: flatRateStorage.notes || null
              });
            } catch (agreementErr) {
              console.error('Error creating flat-rate storage agreement:', agreementErr);
              setError('Customer created but flat-rate storage agreement failed to save. You can create it from the customer detail page.');
            }
          }
        }
      }
      
      // Navigate to the new customer's detail page (or back to
      // list for edits). For new customers, the companyId was
      // extracted from the create response above.
      if (!isEditing && (response?.data?.company?.id || response?.company?.id)) {
        const newCompanyId = response?.data?.company?.id || response?.company?.id;
        navigate(`/admin/customers/${newCompanyId}`);
      } else {
        navigate('/admin/customers');
      }
    } catch (err) {
      console.error('Error submitting customer data:', err);
      setError(err.response?.data?.message || `Failed to ${isEditing ? 'update' : 'create'} customer. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // API connection check
  useEffect(() => {
    const checkApiConnection = async () => {
      try {
        // Basic connectivity check
        await adminAPI.getCustomers({ page: 1, limit: 1 });
      } catch (err) {
        if (err.response?.status === 401) {
          setError('Authentication required. Please log in again.');
          setTimeout(() => navigate('/login'), 2000);
        }
      }
    };
    
    checkApiConnection();
  }, [navigate]);

  if (loading && isEditing) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Get current states/provinces based on selected country
  const availableStates = getStatesForCountry(companyData.address.country);

  // Customer creation is billing-role gated on the backend (RF15):
  // the wizard would otherwise create the company + user + welcome
  // email and only then 403 on the fee step, leaving a partial live
  // customer. Editing stays open to internal staff.
  if (!isEditing && !canCreateCustomer) {
    return (
      <div className="max-w-2xl mx-auto py-12 px-4">
        <div className="rounded-md bg-yellow-50 border border-yellow-200 p-4 text-sm text-yellow-800">
          Creating customers requires billing privileges (admin, manager, or accounting).
        </div>
      </div>
    );
  }
  return (
    <div className="max-w-4xl mx-auto">
      {/* Fixed Header Section */}
      <div className="flex flex-col gap-3 mb-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-semibold text-gray-900">
              {isEditing ? 'Edit Customer' : 'Add New Customer'}
            </h1>
            <p className="mt-2 text-sm text-gray-600">
              Step {step} of {isEditing ? 2 : 3}: {
                step === 1 ? 'Company Information' : 
                step === 2 ? 'Primary Contact Information' :
                'Destination Warehouses & Fees'
              }
            </p>
          </div>
          <div className="flex-shrink-0">
            <button
              onClick={() => navigate('/admin/customers')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <StepProgress
        steps={isEditing
          ? [{ id: 1, name: 'Company Info' }, { id: 2, name: 'Primary Contact' }]
          : [{ id: 1, name: 'Company Info' }, { id: 2, name: 'Primary Contact' }, { id: 3, name: 'Warehouses & Fees' }]
        }
        currentStep={step}
      />

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <form onSubmit={step === (isEditing ? 2 : 3) ? handleSubmit : (e) => e.preventDefault()} className="bg-white shadow-md rounded-lg p-6">
        {/* Step 1: Company Information */}
        {step === 1 && (
          <>
            {/* Company Basic Info */}
            <div className="mb-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Company Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={companyData.name}
                    onChange={handleCompanyChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                    Company Phone
                  </label>
                  <input
                    type="text"
                    id="phone"
                    name="phone"
                    value={companyData.phone}
                    onChange={handleCompanyChange}
                    placeholder="+1-xxx-xxx-xxxx"
                    className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      phoneError ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {phoneError && (
                    <p className="mt-1 text-sm text-red-600">{phoneError}</p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="notificationEmail" className="block text-sm font-medium text-gray-700">
                    Default Notification Email (Optional)
                  </label>
                  <input
                    type="email"
                    id="notificationEmail"
                    name="notificationEmail"
                    value={companyData.notificationEmail}
                    onChange={handleCompanyChange}
                    placeholder="ops@customer.com"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Used as the customer-level fallback when a request creator email is unavailable.
                  </p>
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="notificationEmailsText" className="block text-sm font-medium text-gray-700">
                    Additional Notification Emails (Optional)
                  </label>
                  <textarea
                    id="notificationEmailsText"
                    name="notificationEmailsText"
                    rows={3}
                    value={companyData.notificationEmailsText}
                    onChange={handleCompanyChange}
                    placeholder={'ops@customer.com\nwarehouse@customer.com'}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Additional operational recipients for customer-level emails such as request updates, invoices, manifest updates, and cross-dock updates. Enter one email per line or separate with commas.
                  </p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Enabled Services
                  </label>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                    {serviceCapabilityOptions.map(([key, label]) => (
                      <label key={key} className="flex items-center space-x-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={!!companyData.serviceCapabilities[key]}
                          onChange={(e) => setCompanyData(prev => ({
                            ...prev,
                            serviceCapabilities: {
                              ...prev.serviceCapabilities,
                              [key]: e.target.checked
                            }
                          }))}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <label className="flex items-center space-x-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={!!companyData.serviceCapabilities.require_request_approval}
                        onChange={(e) => setCompanyData(prev => ({
                          ...prev,
                          serviceCapabilities: {
                            ...prev.serviceCapabilities,
                            require_request_approval: e.target.checked
                          }
                        }))}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span>Require approval for outbound requests</span>
                    </label>
                    <p className="mt-1 text-xs text-gray-500 ml-6">
                      When enabled, customer-created ship, pick &amp; pack, and kit build requests
                      require admin approval before work begins.
                    </p>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <div className="flex items-center gap-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Default Receiving Instructions
                    </label>
                    <span
                      title="These defaults prefill new ASNs and manifests. The actual warehouse instructions belong to each inbound request."
                      className="inline-flex h-5 w-5 cursor-help items-center justify-center rounded-full border border-gray-300 text-xs font-medium text-gray-500"
                    >
                      i
                    </span>
                  </div>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      ['require_serial_numbers_on_receive', 'Require serial numbers at receiving'],
                      ['require_mac_addresses_on_receive', 'Require MAC addresses at receiving'],
                      ['relabel_on_receive', 'Default to relabel on receipt']
                    ].map(([key, label]) => (
                      <label key={key} className="flex items-center space-x-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={!!companyData.receivingPreferences[key]}
                          onChange={(e) => setCompanyData(prev => ({
                            ...prev,
                            receivingPreferences: {
                              ...prev.receivingPreferences,
                              [key]: e.target.checked
                            }
                          }))}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Customer Email Notification Preferences
                  </label>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      ['request_created', 'Request Created'],
                      ['request_status', 'Request Status'],
                      ['invoice_created', 'Invoice Created'],
                      ['invoice_status', 'Invoice Status'],
                      ['manifest_updates', 'Manifest Updates'],
                      ['crossdock_updates', 'Cross-dock Updates'],
                      ['receiving_updates', 'Receiving Updates'],
                      ['shipment_updates', 'Shipment Updates']
                    ].map(([key, label]) => (
                      <label key={key} className="flex items-center space-x-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={!!companyData.notificationPreferences[key]}
                          onChange={(e) => setCompanyData(prev => ({
                            ...prev,
                            notificationPreferences: {
                              ...prev.notificationPreferences,
                              [key]: e.target.checked
                            }
                          }))}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Company Address */}
            <div className="mb-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Company Address</h2>
              <p className="mb-3 text-sm text-gray-500">
                Operational company address for this account. Zoho remains the source of truth for invoice billing addresses once the customer is linked.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label htmlFor="address.street" className="block text-sm font-medium text-gray-700">
                    Street Address
                  </label>
                  <input
                    type="text"
                    id="address.street"
                    name="address.street"
                    value={companyData.address.street}
                    onChange={handleCompanyChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="address.addressLine2" className="block text-sm font-medium text-gray-700">
                    Address Line 2
                  </label>
                  <input
                    type="text"
                    id="address.addressLine2"
                    name="address.addressLine2"
                    value={companyData.address.addressLine2}
                    onChange={handleCompanyChange}
                    placeholder="Apartment, suite, unit, building, floor, etc."
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="address.city" className="block text-sm font-medium text-gray-700">
                    City
                  </label>
                  <input
                    type="text"
                    id="address.city"
                    name="address.city"
                    value={companyData.address.city}
                    onChange={handleCompanyChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="address.country" className="block text-sm font-medium text-gray-700">
                    Country
                  </label>
                  <select
                    id="address.country"
                    name="address.country"
                    value={companyData.address.country}
                    onChange={handleCompanyChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    {COUNTRIES.map(country => (
                      <option key={country.value} value={country.value}>
                        {country.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="address.state" className="block text-sm font-medium text-gray-700">
                    {companyData.address.country === 'Canada' ? 'Province' : 'State'}
                  </label>
                  <select
                    id="address.state"
                    name="address.state"
                    value={companyData.address.state}
                    onChange={handleCompanyChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select {companyData.address.country === 'Canada' ? 'Province' : 'State'}</option>
                    {availableStates.map(state => (
                      <option key={state.value} value={state.value}>
                        {state.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="address.postalCode" className="block text-sm font-medium text-gray-700">
                    {companyData.address.country === 'Canada' ? 'Postal Code' : 'ZIP Code'}
                  </label>
                  <input
                    type="text"
                    id="address.postalCode"
                    name="address.postalCode"
                    value={companyData.address.postalCode}
                    onChange={handleCompanyChange}
                    placeholder={companyData.address.country === 'Canada' ? 'A1A 1A1' : '12345 or 12345-6789'}
                    className={`mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      postalCodeError ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {postalCodeError && (
                    <p className="mt-1 text-sm text-red-600">{postalCodeError}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Billing Contact */}
            <div className="mb-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Billing Contact</h2>
              <p className="mb-3 text-sm text-gray-500">
                Contact person for billing inquiries. The invoice billing address is pulled from Zoho once a Zoho Customer ID is linked. You can set an override address on the customer detail page after creation.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="billingInfo.billingContactName" className="block text-sm font-medium text-gray-700">
                    Billing Contact Name
                  </label>
                  <input
                    type="text"
                    id="billingInfo.billingContactName"
                    name="billingInfo.billingContactName"
                    value={companyData.billingInfo.billingContactName}
                    onChange={handleCompanyChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="billingInfo.billingContactEmail" className="block text-sm font-medium text-gray-700">
                    Billing Contact Email
                  </label>
                  <input
                    type="email"
                    id="billingInfo.billingContactEmail"
                    name="billingInfo.billingContactEmail"
                    value={companyData.billingInfo.billingContactEmail}
                    onChange={handleCompanyChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="billingInfo.billingContactPhone" className="block text-sm font-medium text-gray-700">
                    Billing Contact Phone
                  </label>
                  <input
                    type="text"
                    id="billingInfo.billingContactPhone"
                    name="billingInfo.billingContactPhone"
                    value={companyData.billingInfo.billingContactPhone}
                    onChange={handleCompanyChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="zohoCustomerId" className="block text-sm font-medium text-gray-700">
                    Zoho Customer ID
                  </label>
                  <input
                    type="text"
                    id="zohoCustomerId"
                    name="zohoCustomerId"
                    value={companyData.zohoCustomerId}
                    onChange={handleCompanyChange}
                    disabled={isEditing && Boolean(companyData.zohoCustomerId)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Admin only. Once set, this ERP link is locked and should not be changed casually.
                  </p>
                </div>
              </div>
            </div>

            {/* Notes & Status */}
            <div className="mb-6">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                    Notes
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    rows="3"
                    value={companyData.notes}
                    onChange={handleCompanyChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  ></textarea>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    name="isActive"
                    checked={companyData.isActive}
                    onChange={handleCompanyChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                    Active Customer
                  </label>
                </div>
              </div>
            </div>

            {/* Step 1 Actions */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleNext}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Next: Primary Contact
              </button>
            </div>
          </>
        )}

        {/* Step 2: Primary Contact Information */}
        {step === 2 && (
          <>
            <div className="mb-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Primary Contact Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={userData.firstName}
                    onChange={handleUserChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={userData.lastName}
                    onChange={handleUserChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="userEmail" className="block text-sm font-medium text-gray-700">
                    Contact Email *
                  </label>
                  <input
                    type="email"
                    id="userEmail"
                    name="email"
                    value={userData.email}
                    onChange={handleUserChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="userPhone" className="block text-sm font-medium text-gray-700">
                    Contact Phone
                  </label>
                  <input
                    type="text"
                    id="userPhone"
                    name="phone"
                    value={userData.phone}
                    onChange={handleUserChange}
                    placeholder="+1-xxx-xxx-xxxx"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                {!isEditing && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800">Password Setup</h3>
                        <div className="mt-2 text-sm text-blue-700">
                          <p>The primary contact will receive a welcome email with a secure link to set their password. No password needs to be entered here.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Step 2 Actions */}
            <div className="flex justify-between">
              <button
                type="button"
                onClick={handlePrevious}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Previous: Company Info
              </button>
              {isEditing ? (
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Updating...
                    </>
                  ) : 'Update Customer'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleNext}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Next: Destination Warehouses
                </button>
              )}
            </div>
          </>
        )}

        {/* Step 3: Destination Warehouse Assignment & Fees (only for new customers) */}
        {step === 3 && !isEditing && (
          <>
            <div className="mb-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Destination Warehouse Access & Fees</h2>
              
              {/* Destination Warehouse Selection */}
              <div className="mb-8">
                <h3 className="text-md font-medium text-gray-900 mb-3">Select Destination Warehouses</h3>
                
                {/* Debug Information */}
                {availableWarehouses.length === 0 && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p className="text-sm text-yellow-700">
                      {loading ? 'Loading warehouses...' : 'No warehouses available. Please contact your administrator.'}
                    </p>
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  {availableWarehouses.map(warehouse => (
                    <div
                      key={warehouse.id}
                      className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                        selectedWarehouses.find(w => w.id === warehouse.id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                      onClick={() => handleWarehouseSelection(warehouse.id)}
                    >
                      <h4 className="font-medium text-gray-900">{warehouse.name}</h4>
                      <p className="text-sm text-gray-600">Code: {warehouse.code}</p>
                      <p className="text-sm text-gray-600">
                        {warehouse.city}, {warehouse.state_province}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Fee Configuration for Selected Warehouses */}
              {selectedWarehouses.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-md font-medium text-gray-900 mb-3">Fee Configuration</h3>
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-blue-700">
                          Fees will be set for all selected destination warehouses ({selectedWarehouses.map(w => w.name).join(', ')}). 
                          You can edit fees per warehouse after customer creation if needed.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Fee categories (simplified catalog). Only
                      Storage and Handling are surfaced here. Storage
                      is rendered as a dedicated block with one row
                      per storage_class (standard + oversize). Legacy
                      fee types are hidden from the wizard. */}
                  <div className="space-y-6">

                    {/* STORAGE: dedicated block with one row per
                        storage class. Matches the admin Customer
                        Detail fees tab layout. */}
                    <div>
                      <div className="mb-3">
                        <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Storage</h4>
                        <p className="mt-1 text-xs text-gray-500">
                          Per-pallet monthly rate. Loose cartons roll up into pallet-equivalents by volume. Volume and long-term discounts can be set per customer after creation.
                        </p>
                      </div>
                      <div className="space-y-4">
                        {STORAGE_CLASSES_IN_WIZARD.map((sc) => {
                          const row = findFeeRow('storage_fee', sc.value);
                          return (
                            <div key={sc.value} className="bg-gray-50 rounded-lg p-4">
                              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {sc.label}
                                  </label>
                                  <div className="text-xs text-gray-500 mb-2">{sc.description}</div>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700">Amount ($ / pallet / mo)</label>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={row?.fee_amount || ''}
                                    onChange={(e) => {
                                      if (row) {
                                        updateCustomerFee(row.id, 'fee_amount', e.target.value);
                                      } else {
                                        setCustomerFees([...customerFees, {
                                          id: Date.now() + Math.random(),
                                          fee_type: 'storage_fee',
                                          fee_amount: e.target.value,
                                          billing_unit: 'per_pallet',
                                          fee_schedule: 'monthly',
                                          storage_class: sc.value,
                                          notes: ''
                                        }]);
                                      }
                                    }}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    placeholder={sc.value === 'standard' ? '25.00' : '50.00'}
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700">Billing Unit</label>
                                  <select
                                    disabled
                                    title="Billing unit is fixed by the fee catalog; other units are not implemented (RF12)"
                                    value={row?.billing_unit || 'per_pallet'}
                                    onChange={(e) => row && updateCustomerFee(row.id, 'billing_unit', e.target.value)}
                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                  >
                                    {BILLING_UNITS.map(unit => (
                                      <option key={unit.value} value={unit.value}>{unit.label}</option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* HANDLING: the remaining fee types (handling_inbound,
                        handling_outbound, labour) render via the
                        generic loop below. */}
                    {['Handling'].map((category) => {
                      const categoryFeeTypes = FEE_TYPES.filter((feeType) => feeType.category === category);
                      if (categoryFeeTypes.length === 0) return null;

                      return (
                        <div key={category}>
                          <div className="mb-3">
                            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">{category}</h4>
                            <p className="mt-1 text-xs text-gray-500">
                              Per-pallet handling fees. Inbound includes serial/MAC/relabel work; outbound includes pick/pack/ship work. Labour is an ad-hoc hourly rate.
                            </p>
                          </div>
                          <div className="space-y-4">
                            {categoryFeeTypes.map(feeType => (
                              <div key={feeType.value} className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {feeType.label}
                            </label>
                            <div className="text-xs text-gray-500 mb-2">
                              {feeType.description || (feeType.required ? 'Leave blank or set $0 for free service' : 'Optional')}
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Amount ($)</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={customerFees.find(f => f.fee_type === feeType.value)?.fee_amount || ''}
                              onChange={(e) => {
                                const existingFeeIndex = customerFees.findIndex(f => f.fee_type === feeType.value);
                                if (existingFeeIndex >= 0) {
                                  updateCustomerFee(customerFees[existingFeeIndex].id, 'fee_amount', e.target.value);
                                } else {
                                  const newFee = {
                                    id: Date.now() + Math.random(),
                                    fee_type: feeType.value,
                                    fee_amount: e.target.value,
                                    billing_unit: feeType.defaultUnit,
                                    fee_schedule: 'one_time',
                                    storage_class: null,
                                    notes: ''
                                  };
                                  setCustomerFees([...customerFees, newFee]);
                                }
                              }}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">Billing Unit</label>
                            <select
                              disabled
                                    title="Billing unit is fixed by the fee catalog; other units are not implemented (RF12)"
                                    value={customerFees.find(f => f.fee_type === feeType.value)?.billing_unit || feeType.defaultUnit}
                              onChange={(e) => {
                                const existingFeeIndex = customerFees.findIndex(f => f.fee_type === feeType.value);
                                if (existingFeeIndex >= 0) {
                                  updateCustomerFee(customerFees[existingFeeIndex].id, 'billing_unit', e.target.value);
                                }
                              }}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            >
                              {BILLING_UNITS.map(unit => (
                                <option key={unit.value} value={unit.value}>{unit.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}

                    {/* Flat-rate storage override toggle. Optional.
                        When enabled, a StorageAgreement row is
                        created after the customer is created. The
                        customer's per-pallet rates above are still
                        saved (so ops can switch back without
                        re-entering) but won't be billed while the
                        flat rate is active. */}
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={flatRateStorage.enabled}
                          onChange={(e) => setFlatRateStorage((s) => ({ ...s, enabled: e.target.checked }))}
                          className="mt-1 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-amber-900">Charge Flat Rate Storage</div>
                          <div className="text-xs text-amber-800 mt-0.5">
                            Override per-pallet billing with a fixed monthly rate for rented sqft. Use this for customers who sign a contract for a set amount of warehouse space. Can be switched back to per-pallet anytime from the customer detail page.
                          </div>
                        </div>
                      </label>
                      {flatRateStorage.enabled && (
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 pl-6">
                          <div>
                            <label className="block text-xs font-medium text-amber-900">Square Feet</label>
                            <input
                              type="number"
                              min="1"
                              value={flatRateStorage.sqft}
                              onChange={(e) => setFlatRateStorage((s) => ({ ...s, sqft: e.target.value }))}
                              placeholder="1000"
                              className="mt-1 block w-full border border-amber-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-amber-900">Monthly Rate ($)</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={flatRateStorage.monthly_amount}
                              onChange={(e) => setFlatRateStorage((s) => ({ ...s, monthly_amount: e.target.value }))}
                              placeholder="2500.00"
                              className="mt-1 block w-full border border-amber-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-amber-900">Notes</label>
                            <input
                              type="text"
                              value={flatRateStorage.notes}
                              onChange={(e) => setFlatRateStorage((s) => ({ ...s, notes: e.target.value }))}
                              placeholder="Optional (contract ref, etc.)"
                              className="mt-1 block w-full border border-amber-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                  </div>
                </div>
              )}
            </div>

            {/* Step 3 Actions */}
            <div className="flex justify-between">
              <button
                type="button"
                onClick={handlePrevious}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Previous: Contact Info
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Creating...
                  </>
                ) : 'Create Customer'}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default CustomerForm; 
